// URL <-> state helpers for the deliverable tab. Filter / sort /
// pagination round-trip through `?filters[status]=overdue,…`,
// `?sort=due&dir=asc`, `?page=2`. The view-mode `?scope=all|contract|
// sow:<id>` carries the user's choice of slice (default All is omitted).
// Empty / default values are stripped from the URL so `/?page=1` and
// `/` look identical to the user.

import type {
  DueFilterValue,
  FilterChipBarFilters,
  RagFilterValue,
} from "./FilterChipBar";
import {
  STATUS_FILTER_VALUES,
  type StatusFilterValue,
} from "./deliverableStatusFilter";
import type { DeliverableOwner } from "../../../../types";
import {
  ALL_SCOPE,
  parseScope,
  serialiseScope,
  type ScopeValue,
} from "../shared/scopeTypes";

export type DeliverableTableView = "grouped" | "sequential";

export interface DeliverableUrlState {
  filters: FilterChipBarFilters;
  search: string;
  sortColumn: "name" | "owner" | "due" | "status" | "rag" | null;
  sortDirection: "ascending" | "descending" | null;
  page: number;
  pageSize: number;
  scope: ScopeValue;
  /** Table-view layout. `sequential` is the default flat table (matching
   *  the milestones tab convention); `grouped` opts in to the
   *  status-driven four-bucket layout. Default is stripped from the URL. */
  view: DeliverableTableView;
}

const DEFAULT_PAGE_SIZE = 10;

// Built from the canonical STATUS_FILTER_VALUES (shared with the chip
// bar) so the URL allow-list can never drift behind the offered options
// again — the drift previously dropped `conditionally-accepted` +
// `awaiting-resubmission`, making them un-tickable.
const STATUS_VALUES = new Set<StatusFilterValue>(STATUS_FILTER_VALUES);
const OWNER_VALUES = new Set<DeliverableOwner>(["supplier", "customer", "joint"]);
const RAG_VALUES = new Set<RagFilterValue>(["red", "amber", "green", "not-set"]);
const DUE_VALUES = new Set<DueFilterValue>([
  "overdue",
  "this-week",
  "next-30-days",
  "future",
]);
const SORT_COLUMNS = new Set(["name", "owner", "due", "status", "rag"]);

function parseList<T extends string>(
  raw: string | null,
  allowed: Set<T>,
): T[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is T => (allowed as Set<string>).has(s));
}

function parseDirection(raw: string | null): "ascending" | "descending" | null {
  if (raw === "asc") return "ascending";
  if (raw === "desc") return "descending";
  return null;
}

// SOW ids are dynamic, so the parser is a passthrough (no allow-list
// gate). The synthetic UNATTACHED_TOKEN and any backend SOW id round-
// trip; unknown tokens sit harmlessly in state until cleared.
function parseSowList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Same shape as parseSowList — milestone ids are dynamic, parser is
// a passthrough. UNLINKED_MILESTONE_TOKEN and real ids round-trip.
function parseMilestoneList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function parseDeliverableUrlState(
  params: URLSearchParams,
): DeliverableUrlState {
  const filters: FilterChipBarFilters = {
    status: parseList(params.get("filters[status]"), STATUS_VALUES),
    owner: parseList(params.get("filters[owner]"), OWNER_VALUES),
    rag: parseList(params.get("filters[rag]"), RAG_VALUES),
    due: parseList(params.get("filters[due]"), DUE_VALUES),
    sow: parseSowList(params.get("filters[sow]")),
    milestone: parseMilestoneList(params.get("filters[milestone]")),
    includeArchived: params.get("filters[archived]") === "1",
    hasFailedAcceptance: params.get("filters[failed]") === "1",
    notAccepted: params.get("filters[notaccepted]") === "1",
  };
  const search = params.get("q") ?? "";
  const sortColumnRaw = params.get("sort");
  const sortColumn =
    sortColumnRaw && SORT_COLUMNS.has(sortColumnRaw)
      ? (sortColumnRaw as DeliverableUrlState["sortColumn"])
      : null;
  const sortDirection = sortColumn ? parseDirection(params.get("dir")) : null;
  const pageRaw = Number(params.get("page") ?? "1");
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1;
  const pageSizeRaw = Number(params.get("pageSize") ?? DEFAULT_PAGE_SIZE);
  const pageSize =
    Number.isFinite(pageSizeRaw) && pageSizeRaw > 0
      ? Math.floor(pageSizeRaw)
      : DEFAULT_PAGE_SIZE;
  const viewRaw = params.get("view");
  const view: DeliverableTableView =
    viewRaw === "grouped" ? "grouped" : "sequential";
  return {
    filters,
    search,
    sortColumn,
    sortDirection,
    page,
    pageSize,
    scope: parseScope(params.get("scope")),
    view,
  };
}

export function serialiseDeliverableUrlState(
  state: DeliverableUrlState,
  base?: URLSearchParams,
): URLSearchParams {
  // Preserve any unrelated params (e.g. ?tab=deliverable, ?item=...).
  const next = new URLSearchParams(base?.toString() ?? "");
  // Reset the keys this state owns.
  const owned = [
    "filters[status]",
    "filters[owner]",
    "filters[rag]",
    "filters[due]",
    "filters[sow]",
    "filters[milestone]",
    "filters[archived]",
    "filters[failed]",
    "filters[notaccepted]",
    "q",
    "sort",
    "dir",
    "page",
    "pageSize",
    "scope",
    "view",
  ];
  for (const k of owned) next.delete(k);
  if (state.filters.status.length) {
    next.set("filters[status]", state.filters.status.join(","));
  }
  if (state.filters.owner.length) {
    next.set("filters[owner]", state.filters.owner.join(","));
  }
  if (state.filters.rag.length) {
    next.set("filters[rag]", state.filters.rag.join(","));
  }
  if (state.filters.due.length) {
    next.set("filters[due]", state.filters.due.join(","));
  }
  if (state.filters.sow.length) {
    next.set("filters[sow]", state.filters.sow.join(","));
  }
  if (state.filters.milestone.length) {
    next.set("filters[milestone]", state.filters.milestone.join(","));
  }
  if (state.filters.includeArchived) {
    next.set("filters[archived]", "1");
  }
  if (state.filters.hasFailedAcceptance) {
    next.set("filters[failed]", "1");
  }
  if (state.filters.notAccepted) {
    next.set("filters[notaccepted]", "1");
  }
  if (state.search.trim()) next.set("q", state.search);
  if (state.sortColumn && state.sortDirection) {
    next.set("sort", state.sortColumn);
    next.set("dir", state.sortDirection === "ascending" ? "asc" : "desc");
  }
  if (state.page > 1) next.set("page", String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    next.set("pageSize", String(state.pageSize));
  }
  const scopeRaw = serialiseScope(state.scope);
  if (scopeRaw !== null) next.set("scope", scopeRaw);
  // Default `sequential` is stripped from the URL; only `grouped` writes.
  if (state.view === "grouped") next.set("view", "grouped");
  return next;
}

export const DELIVERABLE_DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export function emptyDeliverableUrlState(): DeliverableUrlState {
  return {
    filters: {
      status: [],
      owner: [],
      rag: [],
      due: [],
      sow: [],
      milestone: [],
      includeArchived: false,
      hasFailedAcceptance: false,
      notAccepted: false,
    },
    search: "",
    sortColumn: null,
    sortDirection: null,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    scope: ALL_SCOPE,
    view: "sequential",
  };
}
